import { useState } from 'react';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { PANEL_KEYS, type PanelKeySchema } from '@wrapforce/shared/schemas/pricing';
import type { VehicleRefSource, VehicleServiceKind, WorkspaceVehicleUpdate } from '@wrapforce/shared/schemas/pricing';
import type { VehiclesPanelRow } from '@wrapforce/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { SIZE_CLASS_LABEL_KEYS } from '@/features/quotes/pricing/VehiclePicker';
import { useUpdateWorkspaceVehicle } from '@/features/quotes/pricing/pricingApi';
import {
  useDeleteVehiclePanelOverride, useDeleteVehicleServiceQuantity,
  usePutVehicleDisassemblyOverride, usePutVehiclePanelOverride, usePutVehicleServiceQuantity,
  useVehiclePanelPricing, useVehicleServiceQuantities,
} from './api';
import { HoursField } from './VehiclesTab';
import { StickySaveBar } from './StickySaveBar';
import { PANEL_LABEL_KEYS } from './PanelPricingTab';

const SOURCE_BADGE_STYLE: Record<VehiclesPanelRow['source'], string> = {
  catalog: 'bg-surface-2 text-muted',
  custom: 'bg-accent/15 text-accent-ink',
  overridden: 'bg-warning/15 text-warning',
};

const SECTIONS = ['panel-pricing', 'disassembly', 'ceramic', 'tint', 'ppf'] as const;
type SectionKey = (typeof SECTIONS)[number];
const SECTION_LABEL_KEYS: Record<SectionKey, string> = {
  'panel-pricing': 'vehicleDetail.section.panelPricing',
  disassembly: 'vehicleDetail.section.disassembly',
  ceramic: 'vehicleDetail.section.ceramic',
  tint: 'vehicleDetail.section.tint',
  ppf: 'vehicleDetail.section.ppf',
};

export function VehicleDetailScreen({
  workspaceId, vehicleId, source, row, onBack, onResetOverride,
}: {
  workspaceId: string | null;
  vehicleId: string;
  source: VehicleRefSource;
  row: VehiclesPanelRow | null;
  onBack: () => void;
  onResetOverride: (row: VehiclesPanelRow) => Promise<boolean>;
}) {
  const { t } = useTranslation('pricingV2');
  const [section, setSection] = useState<SectionKey>('panel-pricing');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink"
        >
          <ArrowLeft size={16} /> {t('vehicleDetail.back')}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col">
          {row ? (
            <VehicleNameHeading workspaceId={workspaceId} row={row} />
          ) : (
            <span className="font-display text-lg font-bold text-ink">{t('vehicleDetail.untitled')}</span>
          )}
          {row ? <span className="text-sm text-muted">{t(SIZE_CLASS_LABEL_KEYS[row.sizeClass], { ns: 'quotes' })}</span> : null}
        </div>
        {row ? (
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${SOURCE_BADGE_STYLE[row.source]}`}>
            {t(`vehicles.source.${row.source}`)}
          </span>
        ) : null}
      </div>

      <nav className="flex gap-0.5 overflow-x-auto border-b border-border">
        {SECTIONS.map((sc) => (
          <button
            key={sc}
            type="button"
            onClick={() => setSection(sc)}
            className={cn(
              'flex-none whitespace-nowrap px-3 py-2 text-sm font-semibold',
              section === sc ? 'border-b-2 border-ink text-ink' : 'text-muted hover:text-ink',
            )}
          >
            {t(SECTION_LABEL_KEYS[sc])}
          </button>
        ))}
      </nav>

      {section === 'panel-pricing' ? <VehiclePanelPricingSection workspaceId={workspaceId} vehicleId={vehicleId} source={source} /> : null}
      {section === 'disassembly' ? (
        <VehicleDisassemblySection workspaceId={workspaceId} row={row} onResetOverride={onResetOverride} />
      ) : null}
      {section === 'ceramic' ? <VehicleServiceQuantitySection workspaceId={workspaceId} vehicleId={vehicleId} source={source} kind="ceramic" /> : null}
      {section === 'tint' ? <VehicleServiceQuantitySection workspaceId={workspaceId} vehicleId={vehicleId} source={source} kind="tint" /> : null}
      {section === 'ppf' ? <VehicleServiceQuantitySection workspaceId={workspaceId} vehicleId={vehicleId} source={source} kind="ppf" /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel Pricing section - per-vehicle film ft / labour minutes overrides.
// ---------------------------------------------------------------------------
function VehiclePanelPricingSection({
  workspaceId, vehicleId, source,
}: {
  workspaceId: string | null;
  vehicleId: string;
  source: VehicleRefSource;
}) {
  const { t } = useTranslation('pricingV2');
  const { data: cells } = useVehiclePanelPricing(workspaceId, source, vehicleId);
  const putOverride = usePutVehiclePanelOverride(workspaceId, source, vehicleId);
  const deleteOverride = useDeleteVehiclePanelOverride(workspaceId, source, vehicleId);
  const [drafts, setDrafts] = useState<Partial<Record<PanelKeySchema, { ft: string; min: string }>>>({});
  const [saving, setSaving] = useState(false);

  const cellByPanel = new Map((cells ?? []).map((c) => [c.panelKey, c]));

  // A draft is dirty when it parses to values different from the loaded cell.
  function draftChange(panelKey: PanelKeySchema): { ftX10: number; minutes: number } | null {
    const draft = drafts[panelKey];
    const cell = cellByPanel.get(panelKey);
    if (!draft || !cell) return null;
    const ftX10 = draft.ft.trim() === '' ? cell.ftX10 : Math.round(Math.max(0, Number(draft.ft)) * 10);
    const minutes = draft.min.trim() === '' ? cell.minutes : Math.round(Math.max(0, Number(draft.min)));
    if (ftX10 === cell.ftX10 && minutes === cell.minutes) return null;
    return { ftX10, minutes };
  }

  const dirtyPanels = PANEL_KEYS.filter((pk) => draftChange(pk) !== null);

  function setDraft(panelKey: PanelKeySchema, patch: Partial<{ ft: string; min: string }>) {
    const cell = cellByPanel.get(panelKey);
    if (!cell) return;
    setDrafts((prev) => ({
      ...prev,
      [panelKey]: {
        ft: (cell.ftX10 / 10).toFixed(1),
        min: String(cell.minutes),
        ...prev[panelKey],
        ...patch,
      },
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      for (const panelKey of dirtyPanels) {
        const change = draftChange(panelKey);
        if (change) await putOverride.mutateAsync({ panelKey, input: change });
      }
      setDrafts({});
      toast.success(t('vehicleDetail.savedToast'));
    } catch {
      toast.error(t('vehicleDetail.saveError'));
    } finally {
      setSaving(false);
    }
  }

  function resetPanel(panelKey: PanelKeySchema) {
    deleteOverride.mutate(panelKey, {
      onSuccess: () => toast.success(t('vehicleDetail.resetToast')),
      onError: () => toast.error(t('vehicleDetail.saveError')),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('vehicleDetail.section.panelPricing')}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="border-b border-r border-border px-2 py-1.5 text-left font-bold uppercase text-muted">{t('panelPricing.panelHeader')}</th>
              <th className="border-b border-border px-2 py-1.5 text-right font-bold uppercase text-muted">{t('panelPricing.filmHeader')}</th>
              <th className="border-b border-border px-2 py-1.5 text-right font-bold uppercase text-muted">{t('panelPricing.labourHeader')}</th>
              <th className="border-b border-border px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {PANEL_KEYS.map((panelKey) => {
              const cell = cellByPanel.get(panelKey);
              return (
                <tr key={panelKey}>
                  <td className="border-b border-r border-border px-2 py-1.5 font-semibold text-ink">
                    <div className="flex items-center gap-1.5">
                      <span>{t(PANEL_LABEL_KEYS[panelKey])}</span>
                      {cell && cell.overrideLevel !== 'default' ? (
                        <span
                          className={cn(
                            'rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                            cell.overrideLevel === 'vehicle' ? 'bg-accent/10 text-accent' : 'bg-surface-2 text-muted',
                          )}
                        >
                          {t(`vehicleDetail.overrideLevel.${cell.overrideLevel}`)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="border-b border-border px-2 py-1.5 text-right">
                    <Input
                      type="number"
                      min={0}
                      step="0.1"
                      value={drafts[panelKey]?.ft ?? (cell ? (cell.ftX10 / 10).toFixed(1) : '')}
                      className="h-8 text-right"
                      disabled={!cell}
                      onChange={(e) => setDraft(panelKey, { ft: e.target.value })}
                    />
                  </td>
                  <td className="border-b border-border px-2 py-1.5 text-right">
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      value={drafts[panelKey]?.min ?? (cell ? String(cell.minutes) : '')}
                      className="h-8 text-right"
                      disabled={!cell}
                      onChange={(e) => setDraft(panelKey, { min: e.target.value })}
                    />
                  </td>
                  <td className="border-b border-border px-2 py-1.5 text-right">
                    {cell?.overrideLevel === 'vehicle' ? (
                      <button type="button" onClick={() => resetPanel(panelKey)} className="text-xs font-semibold text-muted hover:text-ink">
                        {t('panelPricing.reset')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <StickySaveBar
          isDirty={dirtyPanels.length > 0}
          isSaving={saving}
          onSave={handleSave}
          onDiscard={() => setDrafts({})}
        />
      </CardContent>
    </Card>
  );
}

// Header vehicle name - inline-editable for custom vehicles only (catalog
// rows' identity is platform data shared across workspaces).
function VehicleNameHeading({ workspaceId, row }: { workspaceId: string | null; row: VehiclesPanelRow }) {
  const { t } = useTranslation('pricingV2');
  const queryClient = useQueryClient();
  const updateVehicle = useUpdateWorkspaceVehicle(workspaceId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(`${row.make} ${row.model}`.trim());

  if (row.source !== 'custom') {
    return <span className="font-display text-lg font-bold text-ink">{`${row.make} ${row.model}`.trim()}</span>;
  }

  async function handleSave() {
    if (name.trim() === '') return;
    try {
      await updateVehicle.mutateAsync({ vehicleId: row.id, input: { label: name.trim() } });
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicles-panel'] });
      toast.success(t('vehicles.editedToast'));
      setEditing(false);
    } catch {
      toast.error(t('vehicles.saveError'));
    }
  }

  if (!editing) {
    return (
      <span className="flex items-center gap-2 font-display text-lg font-bold text-ink">
        {`${row.make} ${row.model}`.trim()}
        <button
          type="button"
          onClick={() => { setName(`${row.make} ${row.model}`.trim()); setEditing(true); }}
          aria-label={t('vehicleDetail.editName')}
          className="text-muted hover:text-ink"
        >
          <Pencil size={14} />
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
        className="h-9 max-w-xs"
        autoFocus
      />
      <Button size="sm" loading={updateVehicle.isPending} disabled={name.trim() === ''} onClick={handleSave}>
        {t('vehicles.form.save')}
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
        {t('vehicleDetail.cancel')}
      </Button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Disassembly section - the L1/L2/L3 editing formerly in EditVehicleDialog.
// ---------------------------------------------------------------------------
function VehicleDisassemblySection({
  workspaceId, row, onResetOverride,
}: {
  workspaceId: string | null;
  row: VehiclesPanelRow | null;
  onResetOverride: (row: VehiclesPanelRow) => Promise<boolean>;
}) {
  const { t } = useTranslation('pricingV2');
  const updateVehicle = useUpdateWorkspaceVehicle(workspaceId);
  const putOverride = usePutVehicleDisassemblyOverride(workspaceId);

  const [l1Minutes, setL1Minutes] = useState(row?.l1Minutes ?? 0);
  const [l2Minutes, setL2Minutes] = useState(row?.l2Minutes ?? 0);
  const [l3Minutes, setL3Minutes] = useState(row?.l3Minutes ?? 0);

  if (!row) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted">{t('vehicleDetail.untitled')}</CardContent>
      </Card>
    );
  }

  const isCustom = row.source === 'custom';
  const saving = updateVehicle.isPending || putOverride.isPending;

  async function handleSave() {
    if (!row) return;
    try {
      if (isCustom) {
        const input: WorkspaceVehicleUpdate = {
          label: `${row.make} ${row.model}`.trim(),
          sizeClass: row.sizeClass,
          l1DisassemblyMinutes: l1Minutes,
          l2DisassemblyMinutes: l2Minutes,
          l3DisassemblyMinutes: l3Minutes,
        };
        await updateVehicle.mutateAsync({ vehicleId: row.id, input });
      } else {
        await putOverride.mutateAsync({ vehicleId: row.id, input: { l1Minutes, l2Minutes, l3Minutes } });
      }
      toast.success(t('vehicles.editedToast'));
    } catch {
      toast.error(t('vehicles.saveError'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('vehicleDetail.section.disassembly')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-3">
          <HoursField id="vd-l1" label={t('vehicles.form.l1Hours')} minutes={l1Minutes} onChange={setL1Minutes} />
          <HoursField id="vd-l2" label={t('vehicles.form.l2Hours')} minutes={l2Minutes} onChange={setL2Minutes} />
          <HoursField id="vd-l3" label={t('vehicles.form.l3Hours')} minutes={l3Minutes} onChange={setL3Minutes} />
        </div>

        <div className="flex items-center justify-between gap-2">
          {row.source === 'overridden' ? (
            <button type="button" onClick={() => onResetOverride(row)} className="text-xs font-semibold text-muted hover:text-ink">
              {t('vehicles.resetToDefault')}
            </button>
          ) : <span />}
          <Button type="button" loading={saving} onClick={handleSave}>
            {t('vehicles.form.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ceramic/Tint/PPF sections - how much material this vehicle needs.
// ---------------------------------------------------------------------------
const KIND_TITLE_KEYS: Record<VehicleServiceKind, string> = {
  ceramic: 'vehicleDetail.section.ceramic',
  tint: 'vehicleDetail.section.tint',
  ppf: 'vehicleDetail.section.ppf',
};

const KIND_QUANTITY_LABEL_KEYS: Record<VehicleServiceKind, string> = {
  ceramic: 'vehicleDetail.quantity.ceramicLabel',
  tint: 'vehicleDetail.quantity.tintLabel',
  ppf: 'vehicleDetail.quantity.ppfLabel',
};

function VehicleServiceQuantitySection({
  workspaceId, vehicleId, source, kind,
}: {
  workspaceId: string | null;
  vehicleId: string;
  source: VehicleRefSource;
  kind: VehicleServiceKind;
}) {
  const { t } = useTranslation('pricingV2');
  const { data: quantities } = useVehicleServiceQuantities(workspaceId, source, vehicleId);
  const putQuantity = usePutVehicleServiceQuantity(workspaceId, source, vehicleId);
  const deleteQuantity = useDeleteVehicleServiceQuantity(workspaceId, source, vehicleId);

  const value = quantities ? quantities[kind] : null;
  const [draft, setDraft] = useState<string | null>(null);

  const parsedDraft = draft !== null && draft.trim() !== '' ? Math.max(0, Number(draft)) : null;
  const isDirty = parsedDraft !== null && parsedDraft !== value;

  function handleSave() {
    if (parsedDraft === null) return;
    putQuantity.mutate({ kind, input: { quantity: parsedDraft } }, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t('vehicleDetail.quantity.savedToast'));
      },
      onError: () => toast.error(t('vehicleDetail.quantity.saveError')),
    });
  }

  function clearQuantity() {
    deleteQuantity.mutate(kind, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t('vehicleDetail.quantity.clearedToast'));
      },
      onError: () => toast.error(t('vehicleDetail.quantity.saveError')),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(KIND_TITLE_KEYS[kind])}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`vsq-${kind}`}>{t(KIND_QUANTITY_LABEL_KEYS[kind])}</Label>
          <Input
            id={`vsq-${kind}`}
            type="number"
            min={0}
            step="0.5"
            value={draft ?? (value ?? '')}
            className="max-w-xs"
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          {value !== null ? (
            <button type="button" onClick={clearQuantity} className="text-xs font-semibold text-muted hover:text-ink">
              {t('vehicleDetail.quantity.clear')}
            </button>
          ) : <span />}
          <Button type="button" loading={putQuantity.isPending} disabled={!isDirty} onClick={handleSave}>
            {t('saveBar.saveChanges')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
